import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ErrorService } from '../services/error.service';
import { AuthService } from '../../auth/auth.service';

/**
 * Global error interceptor that handles HTTP errors and displays them to the user
 * 
 * This interceptor:
 * 1. Catches all HTTP errors
 * 2. Extracts detailed error messages from the response body
 * 3. Displays them to the user via the ErrorService
 * 4. Rethrows the error for further handling
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorService = inject(ErrorService);
  const authService = inject(AuthService);
  const router = inject(Router);
  
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      console.log('%c [ERROR INTERCEPTOR] ❌ Error occurred', 'background: #FF0000; color: #ffffff; font-size: 14px; padding: 5px;');
      
      // Handle 401 Unauthorized errors - automatic logout
      if (error.status === 401) {
        console.log('%c [ERROR INTERCEPTOR] 401 Unauthorized - Auto logout triggered', 'background: #FF6B00; color: #ffffff; font-size: 14px; padding: 5px;');
        
        // Clear authentication state (don't send WebSocket event to prevent loops)
        authService.logout(false);
        
        // Navigate to login page
        router.navigate(['/login']);
        
        // Show a user-friendly message
        errorService.showError('Your session has expired. Please log in again.');
        
        // Return without further processing
        return throwError(() => error);
      }
      
      // Try to extract the detailed error message from the response
      let errorMessage = 'An error occurred';
      
      if (error.error) {
        // Check for various error formats
        if (typeof error.error === 'string') {
          // Plain text error
          errorMessage = error.error;
        } else if (error.error.error?.message) {
          // New structured error format with user-friendly message
          errorMessage = error.error.error.message;
        } else if (error.error.message) {
          // Object with message property
          errorMessage = error.error.message;
        } else if (error.error.error) {
          // Object with error property (legacy format)
          errorMessage = error.error.error;
        } else if (error.error.Body) {
          // Body format used by some APIs
          errorMessage = error.error.Body;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Log the extracted error message
      console.log('%c [ERROR INTERCEPTOR] Extracted error message:', 'color: #FF0000;', errorMessage);
      
      // Check if this is an informational message that shouldn't be shown as an error
      if (errorMessage.toLowerCase().includes('disabled') || 
          errorMessage.toLowerCase().includes('sync is currently') ||
          errorMessage.toLowerCase().includes('disconnected')) {
        // Show as info message with auto-close
        errorService.showInfo(errorMessage, true, 4000);
      } else {
        // Show as error message
        errorService.showError(errorMessage);
      }
      
      // Rethrow the error for further handling
      return throwError(() => error);
    })
  );
};
